import React, { useState, useEffect } from 'react';
import { complianceApi } from '@/api/compliance.api';
import { useToast } from '@/contexts/ToastContext';

interface GetEWayBillDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialEwbNo?: string;
  initialSupplierGstin?: string;
}

export const GetEWayBillDetailsModal: React.FC<GetEWayBillDetailsModalProps> = ({
  isOpen,
  onClose,
  initialEwbNo = '',
  initialSupplierGstin = '',
}) => {
  const toast = useToast();
  const [ewbNo, setEwbNo] = useState(initialEwbNo);
  const [supplierGstin, setSupplierGstin] = useState(initialSupplierGstin);
  const [loading, setLoading] = useState(false);
  const [ewbDetails, setEwbDetails] = useState<any | null>(null);
  const [localMeta, setLocalMeta] = useState<any | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setEwbNo(initialEwbNo);
      setSupplierGstin(initialSupplierGstin);
      setEwbDetails(null);
      setLocalMeta(null);
      setErrorMsg(null);
      if (initialEwbNo && initialEwbNo.trim().length >= 10) {
        handleFetch(initialEwbNo.trim(), initialSupplierGstin);
      }
    }
  }, [isOpen, initialEwbNo, initialSupplierGstin]);

  if (!isOpen) return null;

  const handleFetch = async (targetNo?: string, targetGstin?: string) => {
    const noToFetch = (targetNo || ewbNo).trim();
    if (!noToFetch) {
      toast.error('Please enter an E-Way Bill number');
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    try {
      const res: any = await complianceApi.getEWayBillDetails(noToFetch, (targetGstin !== undefined ? targetGstin : supplierGstin).trim());
      if (res?.success && res?.data) {
        setEwbDetails(res.data);
        setLocalMeta(res.local || null);
        toast.success('E-Way Bill details fetched successfully from NIC ✓');
      } else {
        const msg = res?.message || 'Failed to fetch E-Way Bill details';
        setErrorMsg(msg);
        toast.error(msg);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Error fetching E-Way Bill details';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const formatINR = (val: any) => {
    const num = Number(val || 0);
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(num);
  };

  const getStatusBadge = (status: string) => {
    const s = String(status || '').toUpperCase();
    if (s === 'ACT' || s === 'ACTIVE') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (s === 'CNL' || s === 'CANCELLED') return 'bg-rose-50 text-rose-700 border-rose-200';
    if (s === 'EXP' || s === 'EXPIRED') return 'bg-gray-100 text-gray-700 border-gray-200';
    return 'bg-blue-50 text-blue-700 border-blue-200';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden transform transition-all">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-indigo-950 px-6 py-4 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center border border-white/15">
                <i className="ri-shield-check-line text-lg text-emerald-400" />
              </div>
              <div>
                <h3 className="font-semibold text-base">Government NIC E-Way Bill Verification</h3>
                <p className="text-xs text-gray-300">Live lookup via Whitebooks GSP Gateway</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-gray-300 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Search Box */}
          <div className="p-6 border-b border-gray-100 bg-gray-50/50">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleFetch();
              }}
              className="flex flex-col sm:flex-row items-center gap-3"
            >
              <div className="relative flex-1 w-full">
                <i className="ri-hashtag absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Enter 12-digit E-Way Bill Number (e.g. 131012160179)"
                  value={ewbNo}
                  onChange={(e) => setEwbNo(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-300 rounded-xl text-sm font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-xs"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !ewbNo.trim()}
                className="w-full sm:w-auto px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 cursor-pointer shadow-xs whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    <span>Fetching...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-search-line" />
                    <span>Fetch Live Details</span>
                  </>
                )}
              </button>
            </form>

            {errorMsg && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2.5">
                <i className="ri-error-warning-fill text-red-500 text-sm mt-0.5 shrink-0" />
                <div className="flex-1 leading-relaxed">{errorMsg}</div>
              </div>
            )}
          </div>

          {/* Result Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto space-y-5">
            {loading && !ewbDetails && (
              <div className="py-12 flex flex-col items-center justify-center gap-3 text-gray-400">
                <i className="ri-loader-4-line text-3xl animate-spin text-indigo-600" />
                <p className="text-xs font-medium">Querying Government NIC E-Way Bill Portal...</p>
              </div>
            )}

            {!loading && !ewbDetails && !errorMsg && (
              <div className="py-10 text-center text-gray-400 space-y-2">
                <i className="ri-file-search-line text-4xl text-gray-300" />
                <p className="text-sm font-medium text-gray-600">No E-Way Bill Loaded</p>
                <p className="text-xs text-gray-400 max-w-sm mx-auto">
                  Enter an E-Way Bill Number above to fetch real-time registration status, valid date, distance, vehicle info, and party details directly from NIC.
                </p>
              </div>
            )}

            {ewbDetails && (
              <div className="space-y-4">
                {/* Status Card */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-xs flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
                      <i className="ri-qr-code-line text-xl" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 font-medium">E-Way Bill Number</div>
                      <div className="text-base font-bold font-mono text-gray-900">
                        {ewbDetails.ewbNo || ewbDetails.ewayBillNo || ewbNo}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getStatusBadge(ewbDetails.status || 'ACT')}`}>
                      {ewbDetails.status === 'ACT' ? 'ACTIVE' : ewbDetails.status === 'CNL' ? 'CANCELLED' : ewbDetails.status || 'ACTIVE'}
                    </span>
                    {ewbDetails.mode === 'SANDBOX_SIMULATOR' && (
                      <span className="px-2 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
                        SANDBOX
                      </span>
                    )}
                  </div>
                </div>

                {/* Validity & Dates */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="bg-gray-50/80 border border-gray-200 rounded-xl p-3 text-xs space-y-1">
                    <span className="text-gray-500 font-medium">Generated Date</span>
                    <p className="text-gray-900 font-semibold">{ewbDetails.ewbDate || ewbDetails.ewayBillDate || '—'}</p>
                  </div>
                  <div className="bg-gray-50/80 border border-gray-200 rounded-xl p-3 text-xs space-y-1">
                    <span className="text-gray-500 font-medium">Valid Until</span>
                    <p className="text-indigo-600 font-semibold">{ewbDetails.validUpto || ewbDetails.validUntil || '—'}</p>
                  </div>
                  <div className="bg-gray-50/80 border border-gray-200 rounded-xl p-3 text-xs space-y-1 col-span-2 sm:col-span-1">
                    <span className="text-gray-500 font-medium">Actual Distance</span>
                    <p className="text-gray-900 font-semibold">
                      {ewbDetails.actualDist || ewbDetails.distance || ewbDetails.transDistance || '100'} km
                    </p>
                  </div>
                </div>

                {/* Party Details (Supplier & Recipient) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Supplier */}
                  <div className="bg-gray-50/80 border border-gray-200 rounded-xl p-3.5 text-xs space-y-1.5">
                    <div className="flex items-center justify-between text-gray-500 font-medium border-b border-gray-200/60 pb-1">
                      <span>Supplier (From)</span>
                      <span className="font-mono text-gray-700">{ewbDetails.fromGstin || '—'}</span>
                    </div>
                    <p className="text-gray-900 font-semibold">{ewbDetails.fromTrdName || '—'}</p>
                    <p className="text-gray-600 leading-relaxed">
                      {[ewbDetails.fromAddr1, ewbDetails.fromAddr2, ewbDetails.fromPlace, ewbDetails.fromPincode].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>

                  {/* Recipient */}
                  <div className="bg-gray-50/80 border border-gray-200 rounded-xl p-3.5 text-xs space-y-1.5">
                    <div className="flex items-center justify-between text-gray-500 font-medium border-b border-gray-200/60 pb-1">
                      <span>Recipient (To)</span>
                      <span className="font-mono text-gray-700">{ewbDetails.toGstin || '—'}</span>
                    </div>
                    <p className="text-gray-900 font-semibold">{ewbDetails.toTrdName || '—'}</p>
                    <p className="text-gray-600 leading-relaxed">
                      {[ewbDetails.toAddr1, ewbDetails.toAddr2, ewbDetails.toPlace, ewbDetails.toPincode].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>
                </div>

                {/* Transport & Vehicle Details */}
                <div className="bg-gray-50/80 border border-gray-200 rounded-xl p-4 text-xs space-y-3">
                  <div className="flex items-center justify-between text-gray-500 font-medium border-b border-gray-200/60 pb-1.5">
                    <span className="font-semibold text-gray-700">Vehicle & Transport (Part B)</span>
                    <span>Mode: {ewbDetails.transMode === '1' ? 'Road' : ewbDetails.transMode === '2' ? 'Rail' : ewbDetails.transMode === '3' ? 'Air' : ewbDetails.transMode === '4' ? 'Ship' : 'Road'}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <span className="text-gray-500">Vehicle Number</span>
                      <p className="font-mono font-bold text-gray-900 mt-0.5">
                        {ewbDetails.VehiclListDetails?.[0]?.vehicleNo || ewbDetails.vehicleNo || 'Not Assigned (Part A)'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Transporter ID</span>
                      <p className="text-gray-900 font-medium mt-0.5">{ewbDetails.transporterId || '—'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Transporter Name</span>
                      <p className="text-gray-900 font-medium mt-0.5">{ewbDetails.transporterName || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Financial Summary */}
                {(ewbDetails.totalValue || ewbDetails.totInvValue) && (
                  <div className="bg-gray-50/80 border border-gray-200 rounded-xl p-4 text-xs space-y-2">
                    <div className="flex items-center justify-between font-semibold text-gray-700 border-b border-gray-200/60 pb-1.5">
                      <span>Valuation & Tax Breakdown</span>
                      <span className="text-indigo-600">{formatINR(ewbDetails.totalValue || ewbDetails.totInvValue)}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
                      <div><span className="text-gray-500">Taxable Value:</span> <span className="font-medium text-gray-900">{formatINR(ewbDetails.cgstValue ? ewbDetails.totalValue - (ewbDetails.cgstValue * 2) : ewbDetails.totalValue)}</span></div>
                      <div><span className="text-gray-500">CGST:</span> <span className="font-medium text-gray-900">{formatINR(ewbDetails.cgstValue)}</span></div>
                      <div><span className="text-gray-500">SGST:</span> <span className="font-medium text-gray-900">{formatINR(ewbDetails.sgstValue)}</span></div>
                      <div><span className="text-gray-500">IGST:</span> <span className="font-medium text-gray-900">{formatINR(ewbDetails.igstValue)}</span></div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <span>Powered by Whitebooks NIC E-Way Bill API</span>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
